import { Notification, CHAT_MESSAGE_UNREAD, ORDER_STATUS_CHANGED, ORDER_DOCUMENT_UPLOADED, isChatNotification } from 'types/notifications'
import { transformResponse, v3Api } from './axios'

/** OTK domain notification as returned by HTTP (dates are ISO strings). */
export interface OtkNotificationDto {
  id: string
  userId: number
  type: string
  payload: {
    orderId: string
    isInternal?: boolean
    chatId?: string
    resolutionId?: string
    name?: string
    fileName?: string
    fileId?: string
    uploadedByUserId?: number
  }
  createdAt: string
  readAt: string | null
}

export interface ListUnreadResponse {
  notifications: OtkNotificationDto[]
}

const authHeaders = (token: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
})

export const mapOtkNotificationToFe = (item: OtkNotificationDto): Notification | null => {
  const orderId = item?.payload?.orderId
  if (!orderId) return null

  const createdAt = typeof item.createdAt === 'string' ? item.createdAt : new Date((item.createdAt as unknown) as Date).toISOString()

  const type = item.type || CHAT_MESSAGE_UNREAD

  if (type === ORDER_STATUS_CHANGED) {
    return {
      id: item.id,
      type: ORDER_STATUS_CHANGED,
      orderId,
      isInternal: false,
      createdAt,
      resolutionId: item.payload?.resolutionId != null ? String(item.payload.resolutionId) : undefined,
    }
  }

  if (type === ORDER_DOCUMENT_UPLOADED) {
    return {
      id: item.id,
      type: ORDER_DOCUMENT_UPLOADED,
      orderId,
      isInternal: false,
      createdAt,
      fileName: item.payload?.fileName,
    }
  }

  if (type !== CHAT_MESSAGE_UNREAD) {
    return null
  }

  return {
    id: item.id,
    type: CHAT_MESSAGE_UNREAD,
    orderId,
    isInternal: Boolean(item.payload?.isInternal),
    createdAt,
  }
}

/** Socket / legacy shape → FE Notification (adds type for parity). */
export const mapSocketNotificationToFe = (item: { orderId: string; isInternal: boolean; createdAt: string }): Notification => ({
  orderId: item.orderId,
  isInternal: item.isInternal,
  createdAt: item.createdAt,
  type: CHAT_MESSAGE_UNREAD,
})

export const listUnreadNotifications = (token: string): Promise<ListUnreadResponse> =>
  transformResponse(v3Api.get('notifications/v1', { headers: authHeaders(token) }))

export const markNotificationRead = (token: string, notificationId: string): Promise<unknown> =>
  transformResponse(v3Api.post(`notifications/v1/${notificationId}/read`, {}, { headers: authHeaders(token) }))

export const markNotificationsReadByChat = (token: string, chatId: string): Promise<unknown> =>
  transformResponse(v3Api.post('notifications/v1/by-chat/read', { chatId }, { headers: authHeaders(token) }))

export const markAllNotificationsRead = (token: string): Promise<unknown> =>
  transformResponse(v3Api.post('notifications/v1/read-all', {}, { headers: authHeaders(token) }))

/** Same semantics as chat-be `resetOrderNotificationsByUser` (internal + external). */
export const markOrderNotificationsRead = async (token: string, orderId: string): Promise<void> => {
  await Promise.all([markNotificationsReadByChat(token, `${orderId}-internal`), markNotificationsReadByChat(token, `${orderId}-external`)])
}

/** Full inbox (chat + status + document) from OTK. */
export const fetchFeNotificationsFromOtk = async (token: string): Promise<Notification[]> => {
  const response = await listUnreadNotifications(token)
  return (response.notifications || []).map(mapOtkNotificationToFe).filter((item): item is Notification => item !== null)
}

/** Bell / chat badge sync — chat types only (status uses SSE alerts). */
export const fetchFeChatNotificationsFromOtk = async (token: string): Promise<Notification[]> => {
  const list = await fetchFeNotificationsFromOtk(token)
  return list.filter(isChatNotification)
}
